// Клиент API RFM-центра. Сессия — JWT в localStorage (получен по SSO из
// CRM: crm.brasol.ru → /marketing/rfm-center → sms.brasol.ru/#sso=<token>).

const TOKEN_KEY = "rfm_session";
const PARTNER_KEY = "rfm_partner";
const NAME_KEY = "rfm_name";
let sessionRevision = 0;
export const AUTH_TIMEOUT_MS = 15_000;

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string) {
  sessionRevision += 1;
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken() {
  sessionRevision += 1;
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(PARTNER_KEY);
  localStorage.removeItem(NAME_KEY);
}

/** Имя пользователя из ответа /api/sso — для аватара в шапке. */
export function getUserName(): string {
  return localStorage.getItem(NAME_KEY) ?? "";
}

/** partnerId сессии: не пусто = франчайзи (видит только своё). */
export function getPartnerId(): string {
  return localStorage.getItem(PARTNER_KEY) ?? "";
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    /** Разобранное JSON-тело ошибки (если было): например, 409 с данными открытого счёта. */
    public body?: unknown,
  ) {
    super(message);
  }
}

/** Локальная отмена результата после смены аккаунта, а не ошибка сохранения на сервере. */
export class SessionChangedError extends ApiError {
  readonly cancelled = true;
  constructor() {
    super(409, "Сессия изменилась. Обновите данные текущего кабинета.");
    this.name = "SessionChangedError";
  }
}

interface AuthAttempt {
  revision: number;
  token: string | null;
}
function assertCurrentSession({ revision, token }: AuthAttempt) {
  if (revision !== sessionRevision || getToken() !== token)
    throw new SessionChangedError();
}
function beginAuthAttempt(): AuthAttempt {
  return { revision: ++sessionRevision, token: getToken() };
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getToken();
  const revision = sessionRevision;
  const snapshot = { token, revision };
  const res = await fetch(path, {
    ...init,
    headers: {
      ...(init?.body instanceof FormData
        ? {}
        : { "Content-Type": "application/json" }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
  });
  assertCurrentSession(snapshot);
  if (res.status === 401) {
    // Старый запрос может завершиться уже после обмена SSO или входа другой сессии.
    if (token && revision === sessionRevision && getToken() === token) {
      clearToken();
      window.location.reload();
    }
    throw new ApiError(401, "Сессия завершена. Войдите заново.");
  }
  if (!res.ok) {
    const text = await res.text();
    assertCurrentSession(snapshot);
    let detail = text;
    let parsed: unknown;
    try {
      const body = JSON.parse(text) as {
        message?: string | string[];
        error?: string;
      };
      parsed = body;
      detail = Array.isArray(body.message)
        ? body.message.join(" · ")
        : body.message || body.error || text;
    } catch {
      /* Go API также возвращает обычный текст. */
    }
    throw new ApiError(
      res.status,
      detail || `Не удалось выполнить запрос (${res.status})`,
      parsed,
    );
  }
  if (res.status === 204) return undefined as T;
  const data = (await res.json()) as T;
  // JSON мог загружаться во время входа другого пользователя. Не отдаём старые
  // данные вызывающему коду: он может вручную сохранить их в уже новом QueryClient.
  assertCurrentSession(snapshot);
  return data;
}

export const apiGet = <T>(path: string, signal?: AbortSignal) =>
  request<T>(path, { signal });
export const apiPost = <T>(
  path: string,
  body?: unknown,
  signal?: AbortSignal,
) =>
  request<T>(path, {
    method: "POST",
    body: JSON.stringify(body ?? {}),
    signal,
  });
export const apiPatch = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: "PATCH", body: JSON.stringify(body ?? {}) });
export const apiPut = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: "PUT", body: JSON.stringify(body ?? {}) });
export const apiDelete = <T>(path: string) =>
  request<T>(path, { method: "DELETE" });
export const apiUpload = <T>(path: string, file: File) => {
  const body = new FormData();
  body.append("file", file);
  return request<T>(path, { method: "POST", body });
};

/** Ограничиваем весь ответ входа, включая JSON: зависший запрос не оставляет вечный спиннер. */
async function authRequest<T>(
  path: string,
  attempt: AuthAttempt,
  init?: RequestInit,
): Promise<T> {
  const controller = new AbortController();
  let timeout: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      (async () => {
        const res = await fetch(path, { ...init, signal: controller.signal });
        assertCurrentSession(attempt);
        if (!res.ok) {
          const detail =
            path === "/api/crm/login" && [400, 401].includes(res.status)
              ? "Неверный логин или пароль."
              : res.status === 403
                ? "Нет доступа к SMS-центру. Попросите администратора проверить права вашей роли."
                : res.status === 401
                  ? "Ссылка входа или сессия CRM устарела. Войдите заново."
                  : "Сервис входа временно недоступен. Попробуйте ещё раз.";
          throw new ApiError(res.status, detail);
        }
        return res.status === 204
          ? (undefined as T)
          : ((await res.json()) as T);
      })(),
      new Promise<never>((_resolve, reject) => {
        timeout = setTimeout(() => {
          reject(
            new ApiError(
              408,
              "Сервис входа не ответил за 15 секунд. Попробуйте ещё раз.",
            ),
          );
          controller.abort();
        }, AUTH_TIMEOUT_MS);
      }),
    ]);
  } catch (error) {
    assertCurrentSession(attempt);
    if (error instanceof ApiError) throw error;
    throw new ApiError(
      0,
      "Не удалось связаться с сервисом входа. Проверьте соединение и попробуйте ещё раз.",
    );
  } finally {
    if (timeout !== undefined) clearTimeout(timeout);
  }
}

/** Обмен SSO-токена из #sso= на сессию. Более поздний вход/выход отменяет запись старого ответа. */
async function exchangeSsoForAttempt(
  token: string,
  attempt: AuthAttempt,
): Promise<{ name: string }> {
  assertCurrentSession(attempt);
  const data = await authRequest<{
    token: string;
    name: string;
    partnerId?: string;
  }>("/api/sso", attempt, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  });
  assertCurrentSession(attempt);
  if (
    !data ||
    typeof data.token !== "string" ||
    !data.token.trim() ||
    (data.partnerId !== undefined && typeof data.partnerId !== "string")
  )
    throw new ApiError(
      502,
      "Сервис входа вернул неполный ответ. Попробуйте войти ещё раз.",
    );
  setToken(data.token);
  localStorage.setItem(PARTNER_KEY, data.partnerId ?? "");
  localStorage.setItem(NAME_KEY, typeof data.name === "string" ? data.name : "");
  return data;
}

export function exchangeSso(token: string): Promise<{ name: string }> {
  return exchangeSsoForAttempt(token, beginAuthAttempt());
}

/** Сессия CRM из cookie; используется и для тихого входа, и после ввода пароля. */
async function exchangeCrmSessionForAttempt(
  attempt: AuthAttempt,
): Promise<{ name: string }> {
  assertCurrentSession(attempt);
  const data = await authRequest<{ token: string }>("/api/crm/sso", attempt);
  assertCurrentSession(attempt);
  if (!data || typeof data.token !== "string" || !data.token.trim())
    throw new ApiError(502, "CRM не вернула ссылку входа. Попробуйте ещё раз.");
  return exchangeSsoForAttempt(data.token, attempt);
}

export function exchangeCrmSession(): Promise<{ name: string }> {
  return exchangeCrmSessionForAttempt(beginAuthAttempt());
}

export async function loginWithCrm(
  email: string,
  password: string,
): Promise<{ name: string }> {
  const attempt = beginAuthAttempt();
  await authRequest("/api/crm/login", attempt, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  assertCurrentSession(attempt);
  return exchangeCrmSessionForAttempt(attempt);
}

export const fmtInt = (n: number) =>
  new Intl.NumberFormat("ru-RU").format(Math.round(n));
export const fmtMoney = (n: number) =>
  new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 }).format(n) + " ₽";
export const fmtPct = (x: number) =>
  new Intl.NumberFormat("ru-RU", {
    style: "percent",
    maximumFractionDigits: 1,
  }).format(x);
